
import { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Clipboard } from 'lucide-react';
import { useStockAdjustment } from '../hooks/useStockAdjustment';
import StockAdjustmentForm from './stock/StockAdjustmentForm';

export type AdjustmentType = 'add' | 'remove';

export interface StockAdjustmentProps {
  productId: string;
  productName: string;
  currentStock: number;
  onStockUpdated: (newQuantity: number) => void;
}

const StockAdjustment = ({ 
  productId, 
  productName, 
  currentStock, 
  onStockUpdated 
}: StockAdjustmentProps) => {
  const [open, setOpen] = useState(false);
  
  const {
    adjustmentType,
    setAdjustmentType,
    quantity,
    setQuantity,
    reason,
    setReason,
    notes,
    setNotes,
    isSubmitting,
    handleAdjustStock,
  } = useStockAdjustment({
    productId,
    currentStock,
    onStockUpdated,
    onClose: () => setOpen(false)
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Adjust Stock
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Adjust Inventory</DialogTitle>
          <DialogDescription>
            Update the stock level for {productName}. Current stock: {currentStock}
          </DialogDescription>
        </DialogHeader>
        
        <StockAdjustmentForm
          adjustmentType={adjustmentType}
          setAdjustmentType={setAdjustmentType}
          quantity={quantity}
          setQuantity={setQuantity}
          reason={reason}
          setReason={setReason}
          notes={notes}
          setNotes={setNotes}
        />
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleAdjustStock} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Clipboard className="mr-2 h-4 w-4" />
                Update Inventory
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StockAdjustment;
